// Strip internal hostnames, URLs, and stack traces out of errors before
// showing them to end users. Raw errors should still be logged via
// console.error so developers can inspect them in DevTools.

// Leading "HTTP 422: " style prefix from fetch wrappers — strip it; users don't
// need the status code, just the message.
const HTTP_STATUS_PREFIX = /^HTTP\s+\d{3}\s*:?\s*/i;
const URL_PATTERN = /https?:\/\/[^\s)]+/gi;
// Any host containing azurewebsites.net, or our internal *-rag-* / *-api-* hosts,
// or bare hostnames that include the model service's random suffix shape.
const INTERNAL_HOST_PATTERN = /\b[\w-]+\.azurewebsites\.net\b|\bjpro-[\w-]+\b/gi;
// Leftover empty parens after stripping a URL/host: "(...)" or "( )"
const EMPTY_PARENS_PATTERN = /\(\s*\)/g;

const NETWORK_ERROR_FRAGMENTS = [
  'failed to fetch',
  'networkerror',
  'network request failed',
  'load failed',
  'fetch failed',
];

const FRIENDLY_NETWORK_MESSAGE =
  'Network error — please check your connection and try again.';
const FRIENDLY_FALLBACK = 'Something went wrong. Please try again.';

/**
 * Convert any thrown value into a user-safe string.
 * - Removes internal URLs/hostnames
 * - Replaces opaque network errors with a friendly message
 * - Falls back to a generic message rather than leaking raw error text
 */
export function sanitizeErrorMessage(err, fallback = FRIENDLY_FALLBACK) {
  // A non-string message (a Rails `errors` hash, a thrown object) would either
  // blow up on .toLowerCase() below or stringify to "[object Object]". Neither
  // is something a user should read.
  const candidate = err && (err.message ?? err);
  const raw = typeof candidate === 'string' ? candidate : '';
  const lower = raw.toLowerCase();

  if (NETWORK_ERROR_FRAGMENTS.some(fragment => lower.includes(fragment))) {
    return FRIENDLY_NETWORK_MESSAGE;
  }

  let cleaned = raw
    .replace(HTTP_STATUS_PREFIX, '')
    .replace(URL_PATTERN, '')
    .replace(INTERNAL_HOST_PATTERN, '')
    .replace(EMPTY_PARENS_PATTERN, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  cleaned = cleaned.replace(/[\s,;:–—-]+$/g, '').trim();

  return cleaned || fallback;
}

// What we say per status when the server's own text cannot be shown. Keyed by
// status so the user gets something they can act on rather than one blanket line.
const STATUS_MESSAGES = {
  400: 'That request was not valid. Check the details and try again.',
  401: 'Your session is no longer valid. Please sign in again.',
  403: 'You do not have permission to do that.',
  404: 'That item could not be found. Refresh and try again.',
  409: 'That is already in progress or was changed by someone else. Refresh and try again.',
  413: 'That file is too large.',
  422: 'That could not be completed as entered. Check the details and try again.',
  429: 'Too many requests right now. Wait a moment and try again.',
  500: 'The server hit an error completing that. It has been logged. Try again, and tell an admin if it keeps happening.',
  502: 'The server could not be reached. Try again in a moment.',
  503: 'That service is temporarily unavailable. Try again in a moment.',
  504: 'That took too long to finish. Try again.',
};

// Shapes that mean "this text is an internal error, not copy written for a
// user": exception class names, backtrace frames, SQL, Rails' own 500 body.
// Server responses matching any of these are replaced, never displayed.
const INTERNAL_ERROR_PATTERN = new RegExp([
  'Internal Server Error', 'Traceback', 'SQLSTATE', 'undefined method',
  'ActiveRecord::', 'ActionController::', 'ActionView::', 'ActiveModel::',
  'NoMethodError', 'NameError', 'ArgumentError', 'RuntimeError', 'TypeError',
  'PG::', 'Errno::', 'Net::', 'Redis::', 'Faraday::', 'JSON::',
  '#<', '\\.rb:\\d', '\\.py:\\d', '\\.js:\\d', 'stack trace',
  // What a stringified object collapses to. This is the string the UI showed for
  // every empty-bodied 500 before, so it must never be displayable again.
  '\\[object ',
].join('|'), 'i');

function looksInternal(text) {
  return INTERNAL_ERROR_PATTERN.test(text) ||
    text.includes('\n') ||          // multi-line = a dump, not a sentence
    text.length > 240;              // so is anything this long
}

/**
 * The message a user sees for a failed API response.
 *
 * 4xx: the server's text is shown, because the API authors those deliberately
 * (validation messages, "class must be approved for development"). looksInternal
 * stays as a backstop for a 4xx that carries an exception anyway.
 *
 * 5xx: the server's text is shown ONLY when the response marks it `user_facing`.
 * A 5xx body is otherwise an internal failure, and recognizing internal text by
 * its shape cannot be relied on: "StandardError: connection to db.internal
 * refused" reads like a sentence and names a host. So this is an allowlist. The
 * server states that a line was written for a reader, or the reader gets a
 * status-appropriate line. Raw text is always logged, never shown.
 *
 * @param {number} status - HTTP status of the response
 * @param {*} detail - whatever the server sent (may be empty, or not a string)
 * @param {{userFacing?: boolean}} [opts] - userFacing comes from the response body
 * @returns {string} safe to render
 */
export function apiErrorMessage(status, detail, { userFacing = false } = {}) {
  const text = typeof detail === 'string' ? detail.trim() : '';
  if (detail) console.error(`API error ${status}:`, detail);

  const fallback = STATUS_MESSAGES[status] ||
    (status >= 500 ? STATUS_MESSAGES[500] : FRIENDLY_FALLBACK);
  if (!text || looksInternal(text)) return fallback;
  if (status >= 500 && !userFacing) return fallback;
  return sanitizeErrorMessage(text, fallback);
}
